import {
  EyeInvisibleOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useTranslate } from '@refinedev/core';
import { Button, Space, Tooltip } from 'antd';
import type { ReactNode } from 'react';
import {
  CLIENT_NAMES_SHOWN_MS,
  useClientNamesVisibility,
  type ClientNamesVisibility,
} from './useClientNamesVisibility';

export { CLIENT_NAMES_SHOWN_MS, useClientNamesVisibility };
export type { ClientNamesVisibility };

/** The bare id of a person reference: `users/45` and `45` both read "45". */
export const bareClientId = (id?: string | null): string =>
  String(id ?? '').replace(/^users\//, '');

/** First and last name from one full-name string (first word, last word). */
const splitName = (name?: string | null): [string, string] => {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return ['', ''];
  if (words.length === 1) return [words[0], ''];
  return [words[0], words[words.length - 1]];
};

/** The Clients list's initials: first letter of the first name + first letter of the last name. */
export const clientInitials = (first?: string | null, last?: string | null) =>
  `${(first || '').charAt(0)}${(last || '').charAt(0)}`;

type NameSource = {
  id?: string | null;
  /** A full-name string; split into first/last when `firstName`/`lastName` are not given. */
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

const resolve = ({ id, name, firstName, lastName }: NameSource) => {
  const [f, l] =
    firstName || lastName ? [firstName ?? '', lastName ?? ''] : splitName(name);
  const full = [f, l].filter(Boolean).join(' ');
  return { id: bareClientId(id), full, initials: clientInitials(f, l) };
};

/** "45 · AK" — the id and initials; just the id (or just the initials) when the other is missing. */
export const clientShortLabel = (source: NameSource): string => {
  const { id, initials } = resolve(source);
  return [id, initials].filter(Boolean).join(' · ');
};

/**
 * Text for a client filter option (antd column `filters`): "45 · AK" until Show names is on, then
 * "45 · Anna Keller". A missing name is just the id.
 */
export const clientFilterText = (
  client: {
    _id?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  },
  shown: boolean,
): string => {
  const { id, full, initials } = resolve({
    id: client._id,
    firstName: client.firstName,
    lastName: client.lastName,
  });
  return [id, shown ? full : initials].filter(Boolean).join(' · ');
};

/**
 * Q-mt-3b A (founder, 2026-10-03; supersedes the Q-mt-3 A grey bar) — a client on a page that lists
 * several clients. Hidden: "<id> · <initials>" (e.g. "45 · AK") with the full name on hover (antd
 * Tooltip). Shown: the full name. A name that is not there reads just "<id>"; a person-reference key
 * (`users/…`) is never rendered — only its bare id.
 */
export const ClientName = ({
  visible,
  fallback = '—',
  ...source
}: NameSource & { visible: boolean; fallback?: string }) => {
  const { full } = resolve(source);
  const short = clientShortLabel(source);
  if (visible) {
    return <span data-testid="client-name">{full || short || fallback}</span>;
  }
  const label = short || fallback;
  return (
    <Tooltip title={full || undefined}>
      <span data-testid="client-name-short">{label}</span>
    </Tooltip>
  );
};

/** The marker a translated sentence carries where the name goes — see `ClientNameInText`. */
export const NAME_TOKEN = '\u0001';

/**
 * A sentence with a client's name in it ("{{name}} is not connected to you…"): translate it with
 * `{ name: NAME_TOKEN }` and give it here, so the name itself is the same `ClientName` ("id · initials"
 * while hidden) — a name interpolated into the string would leak through any hidden state.
 */
export const ClientNameInText = ({
  text,
  visible,
  ...source
}: NameSource & {
  text: string;
  visible: boolean;
}) => {
  const parts = text.split(NAME_TOKEN);
  const out: ReactNode[] = [];
  parts.forEach((part, i) => {
    if (i > 0)
      out.push(<ClientName key={`n${i}`} {...source} visible={visible} />);
    if (part) out.push(part);
  });
  return <>{out}</>;
};

/**
 * Q-mt-3 A — "Show names" / "Hide names" for the whole page, with the Clients list's help icon
 * (the PII tip). The button reads "Hide names" while they are shown.
 */
export const ShowNamesButton = ({
  visible,
  onToggle,
  testId,
}: {
  visible: boolean;
  onToggle: () => void;
  testId: string;
}) => {
  const t = useTranslate();
  return (
    <Space size={5}>
      <Tooltip title={t('clientNames.tip')}>
        <QuestionCircleOutlined />
      </Tooltip>
      <Button
        icon={visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
        onClick={onToggle}
        data-testid={testId}
      >
        {t(visible ? 'clientNames.hide' : 'clientNames.show')}
      </Button>
    </Space>
  );
};
